import React from "react";

import Modal from "../../../shared/ui/Modal";
import { toast } from "../../../shared/ui/toast";
import type { AuthContextType } from "../../auth/AuthContext";
import type { ItemGroupSummary } from "../../item-groups/item-groups.api";
import { updateItemPlacement } from "../items.api";

export interface ItemPlacementEditTarget {
  id: string | number;
  name?: string;
  itemgroup?: { id: string; name: string } | null;
}

interface ItemListPlacementEditModalProps {
  auth: AuthContextType;
  item: ItemPlacementEditTarget;
  itemGroups: ItemGroupSummary[];
  previousItem: ItemPlacementEditTarget | null;
  nextItem: ItemPlacementEditTarget | null;
  onHide: () => void;
  onComplete: () => Promise<void>;
}

export default function ItemListPlacementEditModal({
  auth,
  item,
  itemGroups,
  previousItem,
  nextItem,
  onHide,
  onComplete,
}: ItemListPlacementEditModalProps) {
  const currentGroupId = item.itemgroup?.id ?? "";
  const [itemGroupId, setItemGroupId] = React.useState(currentGroupId);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const groupChanged = itemGroupId !== currentGroupId;

  const runAction = async (action: "group" | "swap", otherItem?: ItemPlacementEditTarget) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      if (action === "group") {
        await updateItemPlacement(auth, {
          action: "move_group",
          item_id: String(item.id),
          itemgroup_id: itemGroupId || null,
        });
        toast.success("상품을 선택한 그룹의 맨 아래로 이동했습니다.");
      } else if (otherItem) {
        await updateItemPlacement(auth, {
          action: "swap",
          item_id: String(item.id),
          other_item_id: String(otherItem.id),
        });
        toast.success("상품 순서를 변경했습니다.");
      }
      await onComplete();
      if (action === "group") onHide();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "정렬 위치 변경에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show onHide={() => { if (!isSubmitting) onHide(); }} backdrop="static" size="sm">
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>정렬 위치 수정</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
        <div className="mb-3">
          <label className="form-label">상품</label>
          <div>{item.name || `상품 ${item.id}`}</div>
        </div>
        <div className="mb-3">
          <label htmlFor="item-list-edit-group" className="form-label">상품그룹</label>
          <select
            id="item-list-edit-group"
            className="form-select"
            value={itemGroupId}
            onChange={(event) => setItemGroupId(event.target.value)}
            disabled={isSubmitting}
          >
            <option value="">그룹 없음</option>
            {itemGroups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm mt-2"
            onClick={() => void runAction("group")}
            disabled={!groupChanged || isSubmitting}
          >
            선택한 그룹의 맨 아래로 이동
          </button>
        </div>
        <div>
          <div className="form-label">그룹 내 순서</div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary flex-fill"
              onClick={() => { if (previousItem) void runAction("swap", previousItem); }}
              disabled={groupChanged || !previousItem || isSubmitting}
            >
              한 칸 위
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary flex-fill"
              onClick={() => { if (nextItem) void runAction("swap", nextItem); }}
              disabled={groupChanged || !nextItem || isSubmitting}
            >
              한 칸 아래
            </button>
          </div>
          {groupChanged && <div className="form-text">그룹 이동을 저장한 뒤 순서를 변경할 수 있습니다.</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isSubmitting}>닫기</button>
      </Modal.Footer>
    </Modal>
  );
}
